import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { Coins, X } from "@phosphor-icons/react";

export default function GiftPicker({ receiverId, videoId, liveId, onClose, onSent }) {
    const [gifts, setGifts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(null);

    useEffect(() => {
        let cancelled = false;
        api.get("/gifts/catalog")
            .then(({ data }) => {
                if (cancelled) return;
                setGifts(Array.isArray(data) ? data : []);
            })
            .catch(() => { /* fall through to empty */ })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const send = async (gift) => {
        setSending(gift.slug);
        try {
            const { data } = await api.post("/gifts/send", {
                receiver_id: receiverId,
                video_id: videoId,
                live_id: liveId,
                gift_type: gift.slug,
            });
            toast.success(`Sent ${gift.name}! ${data.coins} coins left`);
            // Give the parent the gift metadata so it can trigger local animation immediately
            onSent?.(data.gift || gift);
            onClose();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally {
            setSending(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="w-full md:max-w-md bg-wta-surface border-t md:border border-wta-border md:rounded-md">
                <div className="flex items-center justify-between p-4 border-b border-wta-border">
                    <div className="font-heading text-xl tracking-wider">Send a Gift</div>
                    <button data-testid="close-gift-picker" onClick={onClose} className="text-wta-muted hover:text-white"><X size={20} /></button>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-wta-muted text-sm">Loading gifts…</div>
                ) : gifts.length === 0 ? (
                    <div className="p-8 text-center text-wta-muted text-sm">No gifts available yet.</div>
                ) : (
                    <div className="grid grid-cols-3 gap-3 p-4 max-h-[60vh] overflow-y-auto">
                        {gifts.map((g) => (
                            <button
                                key={g.slug}
                                onClick={() => send(g)}
                                disabled={sending !== null}
                                data-testid={`gift-${g.slug}`}
                                className="bg-wta-surface2 border border-wta-border rounded-sm p-3 flex flex-col items-center gap-2 hover:border-wta-primary disabled:opacity-50 transition-colors"
                                style={{ borderColor: sending === g.slug ? g.color : undefined }}
                            >
                                <div className="text-3xl h-10 flex items-center justify-center" style={{ filter: `drop-shadow(0 0 8px ${g.color || "#FFD700"}55)` }}>
                                    {g.image_url ? (
                                        <img src={g.image_url} alt="" className="w-9 h-9 object-contain" />
                                    ) : (
                                        <span>{g.emoji || "🎁"}</span>
                                    )}
                                </div>
                                <div className="font-heading text-sm tracking-wider">{g.name}</div>
                                <div className="flex items-center gap-1 text-xs text-wta-accent">
                                    <Coins size={12} weight="fill" /> {g.cost}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
                <div className="px-4 pb-4 text-xs text-wta-muted text-center">80% goes to creator · 20% to platform</div>
            </div>
        </div>
    );
}
